import { Stack } from '@mui/material'

import type { Highlight } from '@/shared/types/highlight'
import { MetaText } from '@/components/atoms/MetaText'
import { HighlightCard } from './HighlightCard'

interface Props {
  highlights: Highlight[]
  // 表示順 (asc = 録音開始順、desc = 最新が上)。default desc。
  order?: 'asc' | 'desc'
  // この件数を超えたぶんは半透明で fade。null で全件 opacity=1。
  fadeAfter?: number
  emptyMessage?: string
}

const getOffset = (k: Highlight): number => k.span_sec

export const HighlightList = ({
  highlights,
  order = 'desc',
  fadeAfter,
  emptyMessage = 'まだ抽出されていません',
}: Props) => {
  if (highlights.length === 0) {
    return (
      <MetaText sx={{ py: 2, textAlign: 'center' }}>{emptyMessage}</MetaText>
    )
  }
  const sorted = [...highlights].sort((a, b) => {
    const diff = getOffset(a) - getOffset(b)
    return order === 'asc' ? diff : -diff
  })
  // 「最新 fadeAfter 件は opaque、 それより古い側は fade」を order に応じて反転。
  // - desc (新→古): 上から数えて fadeAfter 件目までが新しい → opaque、 それ以降が古い → fade
  // - asc  (古→新): 下から数えて fadeAfter 件目までが新しい → opaque、 それ以前が古い → fade
  const computeOpacity = (visualIndex: number): number => {
    if (fadeAfter == null) return 1
    const distanceFromNewestEdge =
      order === 'asc' ? sorted.length - 1 - visualIndex : visualIndex
    if (distanceFromNewestEdge < fadeAfter) return 1
    const overflow = distanceFromNewestEdge - fadeAfter
    return Math.max(0.25, 0.6 - overflow * 0.15)
  }
  return (
    // Notion 流「白い紙」: カード地色・枠・罫線を持たず、 行の余白 (ConversationLine の py) と
    // タイポ階層 (話者=muted / 本文=default) だけで 1 件ずつを分割する。
    <Stack spacing={0}>
      {sorted.map((h, idx) => (
        <HighlightCard key={h.id} highlight={h} opacity={computeOpacity(idx)} />
      ))}
    </Stack>
  )
}
